
import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { SEOData } from '../types';
import { SITE_NAME, SITE_URL } from '../constants';
import { SEO_ROUTES } from '../seo-data.js';

const SCHEMA_SCRIPT_SELECTOR = 'script[data-codex-schema="page"]';

const ensureMeta = (selector: string, attributeName: 'name' | 'property', attributeValue: string) => {
  let meta = document.querySelector(selector) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attributeName, attributeValue);
    document.head.appendChild(meta);
  }
  return meta;
};

const ensureCanonicalLink = () => {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  return link;
};

const removePageSchemas = () => {
  document.querySelectorAll(SCHEMA_SCRIPT_SELECTOR).forEach((node) => node.remove());
};

const SEO: React.FC<SEOData> = ({
  title,
  description,
  canonical,
  ogType,
  ogImage,
  noIndex,
  keywords,
  schema,
}) => {
  const { pathname } = useLocation();
  const resolvedKeywords = useMemo(() => {
    if (keywords && keywords.length > 0) {
      return keywords;
    }
    const match = SEO_ROUTES.find((entry) => entry.path === pathname);
    return match?.keywords || [];
  }, [keywords, pathname]);
  const resolvedCanonical = useMemo(() => {
    if (!canonical) {
      return `${SITE_URL}${pathname === '/' ? '/' : pathname}`;
    }
    return canonical.startsWith('http') ? canonical : `${SITE_URL}${canonical}`;
  }, [canonical, pathname]);

  useEffect(() => {
    document.title = `${title} | ${SITE_NAME}`;
    ensureMeta('meta[name="description"]', 'name', 'description').setAttribute('content', description);

    ensureCanonicalLink().setAttribute('href', resolvedCanonical);
    ensureMeta('meta[name="robots"]', 'name', 'robots').setAttribute(
      'content',
      noIndex ? 'noindex,nofollow' : 'index,follow'
    );

    const effectiveOgType = ogType || 'website';
    const effectiveOgImage = ogImage || `${SITE_URL}/AviaGov.svg`;
    ensureMeta('meta[property="og:title"]', 'property', 'og:title').setAttribute('content', `${title} | ${SITE_NAME}`);
    ensureMeta('meta[property="og:description"]', 'property', 'og:description').setAttribute('content', description);
    ensureMeta('meta[property="og:type"]', 'property', 'og:type').setAttribute('content', effectiveOgType);
    ensureMeta('meta[property="og:url"]', 'property', 'og:url').setAttribute('content', resolvedCanonical);
    ensureMeta('meta[property="og:site_name"]', 'property', 'og:site_name').setAttribute('content', SITE_NAME);
    ensureMeta('meta[property="og:image"]', 'property', 'og:image').setAttribute('content', effectiveOgImage);

    ensureMeta('meta[name="twitter:card"]', 'name', 'twitter:card').setAttribute('content', 'summary_large_image');
    ensureMeta('meta[name="twitter:title"]', 'name', 'twitter:title').setAttribute('content', `${title} | ${SITE_NAME}`);
    ensureMeta('meta[name="twitter:description"]', 'name', 'twitter:description').setAttribute('content', description);
    ensureMeta('meta[name="twitter:image"]', 'name', 'twitter:image').setAttribute('content', effectiveOgImage);

    if (resolvedKeywords.length > 0) {
      ensureMeta('meta[name="keywords"]', 'name', 'keywords').setAttribute('content', resolvedKeywords.join(', '));
    }

    removePageSchemas();

    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((item) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.dataset.codexSchema = 'page';
        script.textContent = JSON.stringify(item);
        document.head.appendChild(script);
      });
    }

    return () => {
      removePageSchemas();
    };
  }, [title, description, resolvedCanonical, ogType, ogImage, noIndex, resolvedKeywords, schema]);

  return null;
};

export default SEO;
